import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Mic,
  MicOff,
  ClipboardPlus,
  FileText,
  ImageIcon,
  CreditCard,
  AlertTriangle,
  Info,
  Upload,
} from "lucide-react";

const MAX_MB = 10;
const MIN_SINTOMAS = 10;

function humanSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

const SpeechRecognitionCtor =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

export default function ConsultaNueva() {
  const navigate = useNavigate();

  const [estado, setEstado] = useState(null);
  const [labs, setLabs] = useState([]);
  const [selectedLabs, setSelectedLabs] = useState(new Set());
  const [sintomas, setSintomas] = useState("");
  const [modoEntrada, setModoEntrada] = useState("texto");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [listening, setListening] = useState(false);
  const [recognitionSupported, setRecognitionSupported] = useState(!!SpeechRecognitionCtor);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [estadoResp, labsResp] = await Promise.all([
        api.get("/historia-clinica/estado"),
        api.get("/laboratorios"),
      ]);
      setEstado(estadoResp.data);
      setLabs(labsResp.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  const toggleLab = (id) => {
    setSelectedLabs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const uploadLab = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`El archivo excede ${MAX_MB} MB.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/laboratorios", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLabs((prev) => [data, ...prev]);
      setSelectedLabs((prev) => new Set([...prev, data.id]));
      toast.success(`Cargado: ${data.nombre}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const startListening = () => {
    if (!SpeechRecognitionCtor) {
      toast.warning("Tu navegador no soporta dictado por voz. Usa Chrome o Edge en escritorio.");
      return;
    }
    const rec = new SpeechRecognitionCtor();
    rec.lang = "es-MX";
    rec.interimResults = true;
    rec.continuous = true;
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      toast.error(`Micrófono: ${e.error || "no disponible"}`);
      setListening(false);
    };
    rec.onresult = (event) => {
      let finalTxt = "";
      let interimTxt = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTxt += chunk;
        else interimTxt += chunk;
      }
      if (finalTxt) {
        setSintomas((prev) => (prev ? `${prev} ${finalTxt}`.replace(/\s+/g, " ").trim() : finalTxt.trim()));
      }
    };
    recognitionRef.current = rec;
    setModoEntrada("voz");
    try {
      rec.start();
    } catch (e) {
      toast.error("No se pudo iniciar el micrófono.");
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setListening(false);
  };

  const submit = async () => {
    if (sintomas.trim().length < MIN_SINTOMAS) {
      toast.error(`Describe tus síntomas (mínimo ${MIN_SINTOMAS} caracteres).`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/consultas/iniciar", {
        sintomas: sintomas.trim(),
        laboratorio_ids: Array.from(selectedLabs),
        modo_entrada: modoEntrada,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const msg = formatApiError(e);
      toast.error(msg);
      if (e?.response?.status === 409 && msg.includes("historia")) {
        navigate("/historia-clinica", { replace: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Gate: incomplete historia
  useEffect(() => {
    if (!loading && estado && !estado.complete) {
      toast.warning("Completa tu historia clínica antes de iniciar una consulta.");
      const t = setTimeout(() => navigate("/historia-clinica", { replace: true }), 1500);
      return () => clearTimeout(t);
    }
  }, [estado, loading, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-[#0F2942]">
            <ClipboardPlus size={12} /> Nueva consulta IA
          </div>
          <h1 className="mt-3 font-display text-3xl font-semibold text-[#0F2942] tracking-tight">
            Describe tu cuadro clínico
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-lg">
            Al pagar, procesaremos tu consulta con Gemini usando tu historia clínica y los
            laboratorios adjuntos. Solo se recomiendan medicamentos OTC según GPC de México.
          </p>
        </div>

        {loading ? (
          <div className="rounded-md border border-border bg-white p-4 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : (
          <>
            {/* Historia gate hint */}
            {estado && !estado.complete && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm flex items-start gap-2 mb-4"
                data-testid="consulta-historia-gate"
              >
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <div>
                  Debes completar tu historia clínica antes de iniciar una consulta. Te redirigimos…
                </div>
              </div>
            )}

            <section
              className="rounded-xl border border-border bg-white p-6 sm:p-7 space-y-5"
              data-testid="consulta-form"
            >
              {/* Textarea + mic */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-foreground">Síntomas</label>
                  <span className="text-[11px] text-muted-foreground">
                    {sintomas.length} / mín {MIN_SINTOMAS}
                  </span>
                </div>
                <Textarea
                  value={sintomas}
                  onChange={(e) => {
                    setSintomas(e.target.value);
                    if (modoEntrada !== "voz") setModoEntrada("texto");
                  }}
                  rows={6}
                  placeholder="Ej. Adulta de 30 años con cefalea leve de 1 día, sin fiebre ni náuseas…"
                  data-testid="consulta-sintomas"
                  className="text-sm"
                />
                <div className="mt-2 flex items-center gap-2">
                  {!listening ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={startListening}
                      disabled={!recognitionSupported}
                      data-testid="consulta-mic-start"
                    >
                      <Mic size={14} />{" "}
                      {recognitionSupported ? "Dictar por voz" : "Voz no soportada"}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={stopListening}
                      data-testid="consulta-mic-stop"
                    >
                      <MicOff size={14} /> Detener
                    </Button>
                  )}
                  <span className="text-[11px] text-muted-foreground">
                    Reconocimiento de voz en español (es-MX) — funciona en Chrome/Edge de escritorio.
                  </span>
                </div>
              </div>

              {/* Labs picker */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-foreground">
                    Laboratorios adjuntos ({selectedLabs.size} seleccionados)
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    className="hidden"
                    onChange={(e) => uploadLab(e.target.files?.[0])}
                    data-testid="consulta-lab-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="consulta-lab-upload-btn"
                  >
                    {uploading ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Upload size={14} />
                    )}
                    Subir nuevo
                  </Button>
                </div>
                <ul className="space-y-1.5" data-testid="consulta-labs-list">
                  {labs.length === 0 && (
                    <li className="text-xs text-muted-foreground">
                      Aún no tienes laboratorios cargados. Puedes continuar sin ellos.
                    </li>
                  )}
                  {labs.map((lab) => (
                    <li
                      key={lab.id}
                      className="flex items-center gap-3 rounded-md border border-border bg-white px-3 py-2"
                    >
                      <Checkbox
                        checked={selectedLabs.has(lab.id)}
                        onCheckedChange={() => toggleLab(lab.id)}
                        data-testid={`consulta-lab-check-${lab.id}`}
                      />
                      <div className="w-7 h-7 rounded bg-muted grid place-items-center text-[#0F2942]">
                        {lab.tipo === "application/pdf" ? (
                          <FileText size={14} />
                        ) : (
                          <ImageIcon size={14} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {lab.nombre}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {lab.tipo} · {humanSize(lab.tamano)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Disclaimer */}
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground flex items-start gap-2">
                <Info size={14} className="shrink-0 mt-0.5 text-[#2563EB]" />
                <span>
                  Esta consulta es orientativa y no sustituye a un profesional. Si detectamos
                  banderas rojas, se te derivará al Directorio de Especialistas.
                </span>
              </div>
            </section>

            <div className="mt-6 flex items-center justify-between">
              <Button variant="outline" onClick={() => navigate(-1)} data-testid="consulta-back-btn">
                <ArrowLeft size={14} /> Volver
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || (estado && !estado.complete)}
                className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white h-11 px-5"
                data-testid="consulta-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Creando pago…
                  </>
                ) : (
                  <>
                    <CreditCard size={14} /> Pagar consulta — $39 MXN <ArrowRight size={14} />
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}
